// components/SelfPaced/pdfSource.js
// ──────────────────────────────────────────────────────────────────────────────
// How the PDF viewers hand a file to pdf.js.
//
// pdf.js is given the URL first, so it STREAMS the file — with range requests
// where the storage answers them, which shows page 1 of a 200-page book after a
// megabyte or two and fetches the rest in the background. The viewers used to
// download the whole file into an ArrayBuffer and copy it twice before pdf.js
// saw a byte: a large book held three copies in the tab, which a phone refuses
// ("Unable to render this PDF inline"), and nothing showed until the last byte.
//
// If pdf.js cannot load the URL itself (a CDN host that does not answer CORS
// for fetch), the old route is the fallback: fetch it whole, hand over bytes.
// ──────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { pdfjs } from 'react-pdf';
// pdf.js's own text-layer stylesheet: it positions the invisible, selectable
// text over each page. Without it the text renders visibly on top of the page.
// Here, because every viewer that draws pages imports this module.
import 'react-pdf/dist/Page/TextLayer.css';

const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${pdfjs.version}`;

// One worker for every viewer, matching the installed pdfjs-dist version.
pdfjs.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/build/pdf.worker.min.mjs`;

/**
 * pdf.js options shared by every <Document>. Must stay one stable object —
 * react-pdf reloads the document whenever `options` changes identity.
 * The asset URLs are what scanned books need: JPEG 2000 page images decode
 * through the wasm build, and CJK / non-embedded fonts through cmaps and the
 * standard fonts. Without them those pages render blank.
 */
export const PDF_OPTIONS = Object.freeze({
  cMapUrl: `${PDFJS_CDN}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `${PDFJS_CDN}/standard_fonts/`,
  wasmUrl: `${PDFJS_CDN}/wasm/`,
  iccUrl: `${PDFJS_CDN}/iccs/`,
  rangeChunkSize: 1 << 20,
});

/**
 * The `file` prop for react-pdf's <Document>, plus the error handler that
 * moves to the fallback.
 *
 * @returns {{ file: object|null, onLoadError: Function, failed: boolean, retry: Function }}
 *   `file` is null while the fallback is fetching; `failed` once both routes have.
 */
export function usePdfSource(url) {
  // 'stream' → pdf.js loads the URL · 'buffer' → we fetch it whole · 'failed'
  const [mode, setMode] = useState('stream');
  const [bytes, setBytes] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setMode('stream');
    setBytes(null);
  }, [url, attempt]);

  useEffect(() => {
    if (mode !== 'buffer' || !url) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = await resp.arrayBuffer();
        if (!cancelled) setBytes(new Uint8Array(buf));
      } catch (err) {
        console.error('[PDF] fallback fetch failed:', err);
        if (!cancelled) setMode('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [mode, url]);

  // A STABLE reference is critical: a new object on every render makes
  // react-pdf reload and re-parse the document, snapping back to page 1.
  const file = useMemo(() => {
    if (!url) return null;
    if (mode === 'stream') return { url };
    // pdf.js detaches the buffer it is given, so it gets its own copy.
    if (mode === 'buffer' && bytes) return { data: bytes.slice() };
    return null;
  }, [url, mode, bytes]);

  const onLoadError = useCallback((err) => {
    if (mode === 'stream') {
      console.warn('[PDF] streaming load failed, fetching the whole file instead:', err);
      setMode('buffer');
    } else {
      console.error('[PDF] load failed:', err);
      setMode('failed');
    }
  }, [mode]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { file, onLoadError, failed: mode === 'failed', retry };
}
